<template>
  <div class="member-management-page">
    <!-- Custom Menu Header -->
    <header class="admin-header">
      <div class="admin-header-content">
        <div class="admin-logo">
          <img src="@/assets/logo.png" alt="新加坡福清会馆" />
        </div>
        <nav class="admin-nav">
          <a 
            href="#members" 
            :class="['admin-nav-link', { active: currentPage === 'members' }]"
            @click.prevent="navigateTo('members')"
          >
            会员管理
          </a>
          <a 
            href="#user-management" 
            :class="['admin-nav-link', { active: currentPage === 'user-management' }]"
            @click.prevent="navigateTo('user-management')"
          >
            用户管理
          </a>
        </nav>
        <div class="admin-header-actions">
          <button @click="handleLogout" class="logout-button">退出登录</button>
        </div>
      </div>
    </header>

    <div class="page-content">
      <div class="container">
        <!-- Page Header -->
        <div class="page-header">
          <h1>{{ pageTitle }}</h1>
        </div>

      <!-- Content based on current page -->
      <div v-if="currentPage === 'members'">
        <!-- Member Management Section -->
      <div class="management-section">
        <div class="section-header">
          <h2>会员列表</h2>
          <div class="header-actions">
            <button @click="openAddMemberModal" class="add-button" type="button">
              + 添加会员
            </button>
            <button @click="loadMembers" class="refresh-button" :disabled="loading">
              {{ loading ? '加载中...' : '刷新' }}
            </button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <!-- Search and Filter -->
        <div class="search-filter-bar">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索会员（姓名、电话、邮箱、会员ID）..."
            class="search-input"
            @input="filterMembers"
          />
        </div>

        <!-- Members Table -->
        <div class="members-table-container">
          <table class="members-table" v-if="filteredMembers.length > 0">
            <thead>
              <tr>
                <th>会员ID</th>
                <th>姓名</th>
                <th>电话</th>
                <th>邮箱</th>
                <th>会员类型</th>
                <th>加入日期</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="member in filteredMembers" :key="member.user_id">
                <td>{{ member.member_id }}</td>
                <td>{{ member.name }}</td>
                <td>{{ member.phone }}</td>
                <td>{{ member.email || '-' }}</td>
                <td>{{ member.member_type }}</td>
                <td>{{ member.join_date }}</td>
                <td>
                  <span :class="['status-badge', member.is_active_member ? 'active' : 'inactive']">
                    {{ member.is_active_member ? '活跃' : '未激活' }}
                  </span>
                </td>
                <td>
                  <button 
                    @click="viewMember(member)" 
                    class="action-button view-button"
                  >
                    查看
                  </button>
                  <button 
                    @click="editMember(member)" 
                    class="action-button edit-button"
                  >
                    编辑
                  </button>
                  <button 
                    @click="deleteMember(member)" 
                    class="action-button edit-button delete-button"
                  >
                    删除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-state">
            <p v-if="loading">加载中...</p>
            <p v-else-if="error">错误: {{ error }}</p>
            <p v-else-if="searchQuery">未找到匹配的会员</p>
            <p v-else>暂无会员数据</p>
          </div>
        </div>

        <!-- Pagination (if needed in future) -->
        <div class="pagination" v-if="filteredMembers.length > 0">
          <p class="member-count">共 {{ filteredMembers.length }} 位会员</p>
        </div>
      </div>
      </div>

      <!-- User Management Section -->
      <div v-if="currentPage === 'user-management'" class="user-management-section">
        <div class="management-section">
        <div class="section-header">
          <h2>用户列表</h2>
          <div class="header-actions">
            <button @click="openAddUserModal" class="add-button" type="button">
              + 添加用户
            </button>
            <button @click="loadUsers" class="refresh-button" :disabled="userLoading">
              {{ userLoading ? '加载中...' : '刷新' }}
            </button>
          </div>
        </div>

          <!-- Error Message -->
          <div v-if="userError" class="error-message">
            {{ userError }}
          </div>

          <!-- Search and Filter -->
          <div class="search-filter-bar">
            <input
              v-model="userSearchQuery"
              type="text"
              placeholder="搜索用户（用户名、邮箱）..."
              class="search-input"
              @input="filterUsers"
            />
          </div>

          <!-- Users Table -->
          <div class="members-table-container">
            <table class="members-table" v-if="filteredUsers.length > 0">
              <thead>
                <tr>
                  <th>用户名</th>
                  <th>邮箱</th>
                  <th>状态</th>
                  <th>已验证</th>
                  <th>超级用户</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in filteredUsers" :key="user.id">
                  <td>{{ user.username }}</td>
                  <td>{{ user.email || '-' }}</td>
                  <td>
                    <span :class="['status-badge', user.is_active ? 'active' : 'inactive']">
                      {{ user.is_active ? '活跃' : '未激活' }}
                    </span>
                  </td>
                  <td>
                    <span :class="['status-badge', user.is_verified ? 'verified' : 'unverified']">
                      {{ user.is_verified ? '已验证' : '未验证' }}
                    </span>
                  </td>
                  <td>
                    <span :class="['status-badge', user.is_superuser ? 'superuser' : 'regular']">
                      {{ user.is_superuser ? '是' : '否' }}
                    </span>
                  </td>
                  <td>
                    <button 
                      @click="editUser(user)" 
                      class="action-button edit-button"
                    >
                      编辑
                    </button>
                    <button 
                      @click="activateUser(user)" 
                      class="action-button view-button"
                      :disabled="user.is_active && user.is_verified && user.is_superuser"
                    >
                      激活
                    </button>
                    <button 
                      @click="deleteUser(user)" 
                      class="action-button edit-button delete-button"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-state">
              <p v-if="userLoading">加载中...</p>
              <p v-else-if="userSearchQuery">未找到匹配的用户</p>
              <p v-else>暂无用户数据</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="filteredUsers.length > 0">
            <p class="member-count">共 {{ filteredUsers.length }} 位用户</p>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Add Member Modal -->
    <div v-if="showAddMemberModal" class="modal-overlay" @click.self="closeAddMemberModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>添加新会员</h2>
          <button @click="closeAddMemberModal" class="modal-close">&times;</button>
        </div>
        <form @submit.prevent="submitAddMember" class="modal-form">
          <div class="form-row">
            <div class="form-group">
              <label>姓名 <span class="required">*</span></label>
              <input v-model="newMember.name" type="text" required />
            </div>
            <div class="form-group">
              <label>电话 <span class="required">*</span></label>
              <input v-model="newMember.phone" type="text" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>邮箱</label>
              <input v-model="newMember.email" type="email" />
            </div>
            <div class="form-group">
              <label>会员ID <span class="required">*</span></label>
              <input v-model.number="newMember.member_id" type="number" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>会员类型 <span class="required">*</span></label>
              <input v-model="newMember.member_type" type="text" required />
            </div>
            <div class="form-group">
              <label>加入日期 <span class="required">*</span></label>
              <input v-model="newMember.join_date" type="date" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>性别 <span class="required">*</span></label>
              <select v-model="newMember.gender" required>
                <option value="">请选择</option>
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
            <div class="form-group">
              <label>出生日期 <span class="required">*</span></label>
              <input v-model="newMember.dob" type="date" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>国籍 <span class="required">*</span></label>
              <input v-model="newMember.nationality" type="text" required />
            </div>
            <div class="form-group">
              <label>状态</label>
              <select v-model="newMember.is_active_member">
                <option :value="true">活跃</option>
                <option :value="false">未激活</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>公司</label>
              <input v-model="newMember.company" type="text" />
            </div>
            <div class="form-group">
              <label>职业</label>
              <input v-model="newMember.occupation" type="text" />
            </div>
          </div>
          <div class="form-group">
            <label>居住地址</label>
            <input v-model="newMember.residential_address" type="text" />
          </div>
          <div v-if="memberFormError" class="error-message">
            {{ memberFormError }}
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeAddMemberModal" class="cancel-button">取消</button>
            <button type="submit" class="submit-button" :disabled="memberSubmitting">
              {{ memberSubmitting ? '创建中...' : '创建' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add User Modal -->
    <div v-if="showAddUserModal" class="modal-overlay" @click.self="closeAddUserModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>添加新用户</h2>
          <button @click="closeAddUserModal" class="modal-close">&times;</button>
        </div>
        <form @submit.prevent="submitAddUser" class="modal-form">
          <div class="form-group">
            <label>用户名 <span class="required">*</span></label>
            <input v-model="newUser.username" type="text" required />
          </div>
          <div class="form-group">
            <label>密码 <span class="required">*</span></label>
            <input v-model="newUser.password" type="password" required />
          </div>
          <div class="form-group">
            <label>邮箱</label>
            <input v-model="newUser.email" type="email" />
          </div>
          <div v-if="userFormError" class="error-message">
            {{ userFormError }}
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeAddUserModal" class="cancel-button">取消</button>
            <button type="submit" class="submit-button" :disabled="userSubmitting">
              {{ userSubmitting ? '创建中...' : '创建' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- View Member Details Modal -->
    <div v-if="showViewMemberModal && selectedMember" class="modal-overlay" @click.self="closeViewMemberModal">
      <div class="modal-content view-modal">
        <div class="modal-header">
          <h2>会员详情</h2>
          <button @click="closeViewMemberModal" class="modal-close">&times;</button>
        </div>
        <div class="member-details">
          <div class="detail-section">
            <h3>基本信息</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <label>姓名</label>
                <div class="detail-value">{{ selectedMember.name }}</div>
              </div>
              <div class="detail-item">
                <label>会员ID</label>
                <div class="detail-value">{{ selectedMember.member_id }}</div>
              </div>
              <div class="detail-item">
                <label>电话</label>
                <div class="detail-value">{{ selectedMember.phone }}</div>
              </div>
              <div class="detail-item">
                <label>邮箱</label>
                <div class="detail-value">{{ selectedMember.email || '-' }}</div>
              </div>
              <div class="detail-item">
                <label>性别</label>
                <div class="detail-value">{{ selectedMember.gender }}</div>
              </div>
              <div class="detail-item">
                <label>出生日期</label>
                <div class="detail-value">{{ selectedMember.dob }}</div>
              </div>
              <div class="detail-item">
                <label>国籍</label>
                <div class="detail-value">{{ selectedMember.nationality }}</div>
              </div>
              <div class="detail-item">
                <label>状态</label>
                <div class="detail-value">
                  <span :class="['status-badge', selectedMember.is_active_member ? 'active' : 'inactive']">
                    {{ selectedMember.is_active_member ? '活跃' : '未激活' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3>会员信息</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <label>会员类型</label>
                <div class="detail-value">{{ selectedMember.member_type }}</div>
              </div>
              <div class="detail-item">
                <label>加入日期</label>
                <div class="detail-value">{{ selectedMember.join_date }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3>工作信息</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <label>公司</label>
                <div class="detail-value">{{ selectedMember.company || '-' }}</div>
              </div>
              <div class="detail-item">
                <label>职业</label>
                <div class="detail-value">{{ selectedMember.occupation || '-' }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3>联系信息</h3>
            <div class="detail-grid">
              <div class="detail-item full-width">
                <label>居住地址</label>
                <div class="detail-value">{{ selectedMember.residential_address || '-' }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="closeViewMemberModal" class="cancel-button">关闭</button>
        </div>
      </div>
    </div>

    <!-- Edit Member Modal -->
    <div v-if="showEditMemberModal && editingMember" class="modal-overlay" @click.self="closeEditMemberModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>编辑会员信息</h2>
          <button @click="closeEditMemberModal" class="modal-close">&times;</button>
        </div>
        <form @submit.prevent="submitEditMember" class="modal-form">
          <div class="form-row">
            <div class="form-group">
              <label>姓名 <span class="required">*</span></label>
              <input v-model="editMemberForm.name" type="text" required />
            </div>
            <div class="form-group">
              <label>电话 <span class="required">*</span></label>
              <input v-model="editMemberForm.phone" type="text" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>邮箱</label>
              <input v-model="editMemberForm.email" type="email" />
            </div>
            <div class="form-group">
              <label>会员ID</label>
              <input :value="editingMember.member_id" type="number" disabled />
              <small style="color: var(--text-secondary); font-size: 0.8rem;">会员ID不可修改</small>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>会员类型 <span class="required">*</span></label>
              <input v-model="editMemberForm.member_type" type="text" required />
            </div>
            <div class="form-group">
              <label>加入日期 <span class="required">*</span></label>
              <input v-model="editMemberForm.join_date" type="date" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>性别 <span class="required">*</span></label>
              <select v-model="editMemberForm.gender" required>
                <option value="">请选择</option>
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
            <div class="form-group">
              <label>出生日期 <span class="required">*</span></label>
              <input v-model="editMemberForm.dob" type="date" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>国籍 <span class="required">*</span></label>
              <input v-model="editMemberForm.nationality" type="text" required />
            </div>
            <div class="form-group">
              <label>状态</label>
              <select v-model="editMemberForm.is_active_member">
                <option :value="true">活跃</option>
                <option :value="false">未激活</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>公司</label>
              <input v-model="editMemberForm.company" type="text" />
            </div>
            <div class="form-group">
              <label>职业</label>
              <input v-model="editMemberForm.occupation" type="text" />
            </div>
          </div>
          <div class="form-group">
            <label>居住地址</label>
            <input v-model="editMemberForm.residential_address" type="text" />
          </div>
          <div v-if="editMemberError" class="error-message">
            {{ editMemberError }}
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeEditMemberModal" class="cancel-button">取消</button>
            <button type="submit" class="submit-button" :disabled="editMemberSubmitting">
              {{ editMemberSubmitting ? '更新中...' : '更新' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div v-if="showEditUserModal && editingUser" class="modal-overlay" @click.self="closeEditUserModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>编辑用户信息</h2>
          <button @click="closeEditUserModal" class="modal-close">&times;</button>
        </div>
        <form @submit.prevent="submitEditUser" class="modal-form">
          <div class="form-group">
            <label>用户名 <span class="required">*</span></label>
            <input v-model="editUserForm.username" type="text" required />
          </div>
          <div class="form-group">
            <label>邮箱</label>
            <input v-model="editUserForm.email" type="email" />
          </div>
          <div class="form-group">
            <label>密码</label>
            <input v-model="editUserForm.password" type="password" placeholder="留空则不修改密码" />
            <small style="color: var(--text-secondary); font-size: 0.8rem;">留空则不修改密码</small>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>状态</label>
              <select v-model="editUserForm.is_active">
                <option :value="true">活跃</option>
                <option :value="false">未激活</option>
              </select>
            </div>
            <div class="form-group">
              <label>已验证</label>
              <select v-model="editUserForm.is_verified">
                <option :value="true">是</option>
                <option :value="false">否</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>超级用户</label>
            <select v-model="editUserForm.is_superuser">
              <option :value="true">是</option>
              <option :value="false">否</option>
            </select>
          </div>
          <div v-if="editUserError" class="error-message">
            {{ editUserError }}
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeEditUserModal" class="cancel-button">取消</button>
            <button type="submit" class="submit-button" :disabled="editUserSubmitting">
              {{ editUserSubmitting ? '更新中...' : '更新' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'MemberManagementPage',
  data() {
    return {
      currentUser: null,
      members: [],
      filteredMembers: [],
      users: [],
      filteredUsers: [],
      searchQuery: '',
      userSearchQuery: '',
      loading: false,
      userLoading: false,
      error: null,
      userError: null,
      currentHash: window.location.hash || '#members',
      // Add Member Modal
      showAddMemberModal: false,
      newMember: {
        name: '',
        phone: '',
        email: '',
        residential_address: '',
        member_id: '',
        member_type: '',
        join_date: '',
        is_active_member: true,
        gender: '',
        dob: '',
        nationality: '',
        company: '',
        occupation: ''
      },
      memberFormError: null,
      memberSubmitting: false,
      // Add User Modal
      showAddUserModal: false,
      newUser: {
        username: '',
        password: '',
        email: ''
      },
      userFormError: null,
      userSubmitting: false,
      // View Member Modal
      showViewMemberModal: false,
      selectedMember: null,
      // Edit Member Modal
      showEditMemberModal: false,
      editingMember: null,
      editMemberForm: {
        name: '',
        phone: '',
        email: '',
        residential_address: '',
        member_type: '',
        join_date: '',
        is_active_member: true,
        gender: '',
        dob: '',
        nationality: '',
        company: '',
        occupation: ''
      },
      editMemberError: null,
      editMemberSubmitting: false,
      // Edit User Modal
      showEditUserModal: false,
      editingUser: null,
      editUserForm: {
        username: '',
        email: '',
        password: '',
        is_active: true,
        is_verified: false,
        is_superuser: false
      },
      editUserError: null,
      editUserSubmitting: false
    }
  },
  computed: {
    currentPage() {
      const hash = this.currentHash
      if (hash === '#members' || hash === '#member-management') {
        return 'members'
      }
      if (hash === '#user-management') {
        return 'user-management'
      }
      return 'members' // default
    },
    pageTitle() {
      if (this.currentPage === 'user-management') {
        return '用户管理'
      }
      return '会员管理'
    }
  },
  mounted() {
    // Initialize hash
    this.currentHash = window.location.hash || '#members'
    // Load data based on current page
    console.log('MemberManagement mounted, currentPage:', this.currentPage)
    if (this.currentPage === 'members') {
      this.loadMembers()
    } else if (this.currentPage === 'user-management') {
      this.loadUsers()
    }
    window.addEventListener('hashchange', this.handleHashChange)
  },
  beforeUnmount() {
    window.removeEventListener('hashchange', this.handleHashChange)
  },
  methods: {
    handleHashChange() {
      // Update reactive hash property
      this.currentHash = window.location.hash || '#members'
      // Load data for the new page
      this.$nextTick(() => {
        if (this.currentPage === 'members') {
          this.loadMembers()
        } else if (this.currentPage === 'user-management') {
          this.loadUsers()
        }
      })
    },
    navigateTo(page) {
      let newHash = ''
      if (page === 'members') {
        newHash = '#members'
      } else if (page === 'user-management') {
        newHash = '#user-management'
      }
      
      // Update hash immediately for reactivity
      this.currentHash = newHash
      window.location.hash = newHash
      
      // Load data for the new page
      if (page === 'members') {
        this.loadMembers()
      } else if (page === 'user-management') {
        this.loadUsers()
      }
    },
    async loadMembers() {
      this.loading = true
      this.error = null
      
      try {
        // Fetch all members from the /manage-members/all endpoint
        const members = await api.getAllMembers()
        console.log('Loaded members:', members)
        this.members = members || []
        this.filteredMembers = [...this.members]
        console.log('Members count:', this.members.length)
      } catch (error) {
        this.error = error.message || '加载会员列表失败'
        console.error('Failed to load members:', error)
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        })
        this.members = []
        this.filteredMembers = []
      } finally {
        this.loading = false
      }
    },
    filterMembers() {
      if (!this.searchQuery.trim()) {
        this.filteredMembers = [...this.members]
        return
      }

      const query = this.searchQuery.toLowerCase()
      this.filteredMembers = this.members.filter(member => {
        return (
          member.name.toLowerCase().includes(query) ||
          member.phone.toLowerCase().includes(query) ||
          (member.email && member.email.toLowerCase().includes(query)) ||
          member.member_id.toString().includes(query)
        )
      })
    },
    viewMember(member) {
      this.selectedMember = member
      this.showViewMemberModal = true
    },
    closeViewMemberModal() {
      this.showViewMemberModal = false
      this.selectedMember = null
    },
    editMember(member) {
      this.editingMember = member
      // Pre-populate form with existing member data
      this.editMemberForm = {
        name: member.name || '',
        phone: member.phone || '',
        email: member.email || '',
        residential_address: member.residential_address || '',
        member_type: member.member_type || '',
        join_date: member.join_date || '',
        is_active_member: member.is_active_member !== undefined ? member.is_active_member : true,
        gender: member.gender || '',
        dob: member.dob || '',
        nationality: member.nationality || '',
        company: member.company || '',
        occupation: member.occupation || ''
      }
      this.showEditMemberModal = true
      this.editMemberError = null
    },
    closeEditMemberModal() {
      this.showEditMemberModal = false
      this.editingMember = null
      this.editMemberError = null
    },
    async submitEditMember() {
      this.editMemberSubmitting = true
      this.editMemberError = null

      try {
        const updateData = {
          ...this.editMemberForm,
          join_date: this.editMemberForm.join_date || this.editingMember.join_date
        }
        const updatedMember = await api.updateMember(this.editingMember.user_id, updateData)
        
        // Update member in local list
        const index = this.members.findIndex(m => m.user_id === this.editingMember.user_id)
        if (index !== -1) {
          this.members[index] = updatedMember
          this.filteredMembers = [...this.members]
        }
        
        // Close modal
        this.closeEditMemberModal()
        alert('会员信息更新成功！')
      } catch (error) {
        this.editMemberError = error.message || '更新会员信息失败'
        console.error('Failed to update member:', error)
      } finally {
        this.editMemberSubmitting = false
      }
    },
    async deleteMember(member) {
      if (!confirm(`确定要删除会员 "${member.name}" 吗？\n此操作不可撤销！`)) {
        return
      }

      try {
        await api.deleteMember(member.user_id)
        // Remove member from local list
        this.members = this.members.filter(m => m.user_id !== member.user_id)
        this.filteredMembers = this.filteredMembers.filter(m => m.user_id !== member.user_id)
        alert(`会员 "${member.name}" 已成功删除！`)
      } catch (error) {
        alert(`删除会员失败: ${error.message}`)
        console.error('Failed to delete member:', error)
      }
    },
    async loadUsers() {
      this.userLoading = true
      this.userError = null
      
      try {
        // Fetch all users from the /manage-users/all endpoint
        const users = await api.getAllUsers()
        this.users = users || []
        this.filteredUsers = [...this.users]
      } catch (error) {
        this.userError = error.message || '加载用户列表失败'
        console.error('Failed to load users:', error)
        this.users = []
        this.filteredUsers = []
      } finally {
        this.userLoading = false
      }
    },
    filterUsers() {
      if (!this.userSearchQuery.trim()) {
        this.filteredUsers = [...this.users]
        return
      }

      const query = this.userSearchQuery.toLowerCase()
      this.filteredUsers = this.users.filter(user => {
        return (
          user.username.toLowerCase().includes(query) ||
          (user.email && user.email.toLowerCase().includes(query))
        )
      })
    },
    editUser(user) {
      this.editingUser = user
      // Pre-populate form with existing user data
      this.editUserForm = {
        username: user.username || '',
        email: user.email || '',
        password: '', // Password is always empty (user needs to enter new password)
        is_active: user.is_active !== undefined ? user.is_active : true,
        is_verified: user.is_verified !== undefined ? user.is_verified : false,
        is_superuser: user.is_superuser !== undefined ? user.is_superuser : false
      }
      this.showEditUserModal = true
      this.editUserError = null
    },
    closeEditUserModal() {
      this.showEditUserModal = false
      this.editingUser = null
      this.editUserError = null
    },
    async submitEditUser() {
      this.editUserSubmitting = true
      this.editUserError = null

      try {
        // Prepare update data - only include password if it's provided
        const updateData = {
          username: this.editUserForm.username,
          email: this.editUserForm.email,
          is_active: this.editUserForm.is_active,
          is_verified: this.editUserForm.is_verified,
          is_superuser: this.editUserForm.is_superuser
        }
        
        // Only include password if it's not empty
        if (this.editUserForm.password && this.editUserForm.password.trim() !== '') {
          updateData.password = this.editUserForm.password
        }
        
        const updatedUser = await api.updateUser(this.editingUser.id, updateData)
        
        // Update user in local list
        const index = this.users.findIndex(u => u.id === this.editingUser.id)
        if (index !== -1) {
          this.users[index] = updatedUser
          this.filteredUsers = [...this.users]
        }
        
        // Close modal
        this.closeEditUserModal()
        alert('用户信息更新成功！')
      } catch (error) {
        this.editUserError = error.message || '更新用户信息失败'
        console.error('Failed to update user:', error)
      } finally {
        this.editUserSubmitting = false
      }
    },
    async activateUser(user) {
      if (!confirm(`确定要激活用户 "${user.username}" 吗？\n这将设置：\n- 状态: 活跃\n- 已验证: 是\n- 超级用户: 是`)) {
        return
      }

      try {
        const updatedUser = await api.activateUser(user.id)
        // Update the user in the local list
        const index = this.users.findIndex(u => u.id === user.id)
        if (index !== -1) {
          this.users[index] = updatedUser
          this.filteredUsers = [...this.users]
        }
        alert(`用户 "${user.username}" 已成功激活！`)
      } catch (error) {
        alert(`激活用户失败: ${error.message}`)
        console.error('Failed to activate user:', error)
      }
    },
    async deleteUser(user) {
      if (!confirm(`确定要删除用户 "${user.username}" 吗？\n此操作不可撤销！`)) {
        return
      }

      try {
        await api.deleteUser(user.id)
        // Remove user from local list
        this.users = this.users.filter(u => u.id !== user.id)
        this.filteredUsers = this.filteredUsers.filter(u => u.id !== user.id)
        alert(`用户 "${user.username}" 已成功删除！`)
      } catch (error) {
        alert(`删除用户失败: ${error.message}`)
        console.error('Failed to delete user:', error)
      }
    },
    openAddMemberModal() {
      console.log('Opening add member modal')
      this.showAddMemberModal = true
      this.memberFormError = null
      // Reset form
      this.newMember = {
        name: '',
        phone: '',
        email: '',
        residential_address: '',
        member_id: '',
        member_type: '',
        join_date: '',
        is_active_member: true,
        gender: '',
        dob: '',
        nationality: '',
        company: '',
        occupation: ''
      }
    },
    closeAddMemberModal() {
      this.showAddMemberModal = false
      this.memberFormError = null
    },
    async submitAddMember() {
      this.memberSubmitting = true
      this.memberFormError = null

      try {
        const memberData = {
          ...this.newMember,
          member_id: parseInt(this.newMember.member_id),
          join_date: this.newMember.join_date || new Date().toISOString().split('T')[0]
        }
        const createdMember = await api.createMember(memberData)
        // Add to local list
        this.members.push(createdMember)
        this.filteredMembers = [...this.members]
        // Close modal
        this.closeAddMemberModal()
        alert('会员创建成功！')
      } catch (error) {
        this.memberFormError = error.message || '创建会员失败'
        console.error('Failed to create member:', error)
      } finally {
        this.memberSubmitting = false
      }
    },
    openAddUserModal() {
      console.log('Opening add user modal')
      this.showAddUserModal = true
      this.userFormError = null
      // Reset form
      this.newUser = {
        username: '',
        password: '',
        email: ''
      }
    },
    closeAddUserModal() {
      this.showAddUserModal = false
      this.userFormError = null
    },
    async submitAddUser() {
      this.userSubmitting = true
      this.userFormError = null

      try {
        const createdUser = await api.createUser(this.newUser)
        // Add to local list
        this.users.push(createdUser)
        this.filteredUsers = [...this.users]
        // Close modal
        this.closeAddUserModal()
        alert('用户创建成功！')
      } catch (error) {
        this.userFormError = error.message || '创建用户失败'
        console.error('Failed to create user:', error)
      } finally {
        this.userSubmitting = false
      }
    },
    async handleLogout() {
      if (confirm('确定要退出登录吗？')) {
        try {
          await api.logout()
          window.location.hash = '#login'
          window.location.reload()
        } catch (error) {
          console.error('Logout failed:', error)
          // Still redirect to login even if logout fails
          window.location.hash = '#login'
        }
      }
    }
  }
}
</script>

<style scoped>
.member-management-page {
  min-height: 100vh;
  background: var(--background);
  display: flex;
  flex-direction: column;
}

/* Admin Header */
.admin-header {
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid var(--border-color);
}

.admin-header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
}

.admin-logo img {
  height: 50px;
  width: auto;
  object-fit: contain;
}

.admin-nav {
  display: flex;
  gap: 0;
  flex: 1;
  justify-content: center;
}

.admin-nav-link {
  padding: 0.75rem 2rem;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
}

.admin-nav-link:hover {
  color: var(--primary-color);
  background: var(--surface);
}

.admin-nav-link.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.admin-header-actions {
  display: flex;
  align-items: center;
}

.logout-button {
  padding: 0.5rem 1rem;
  background: var(--text-secondary);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logout-button:hover {
  background: var(--text-primary);
  transform: translateY(-2px);
}

.page-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--primary-color);
  padding-top: 2rem;
}

.page-content {
  flex: 1;
  padding: 2rem 0;
}

.page-header h1 {
  font-size: 2.5rem;
  color: var(--primary-color);
  font-weight: 600;
}

.user-info-card {
  background: var(--surface);
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  border-left: 4px solid var(--primary-color);
}

.user-info-card h2 {
  color: var(--primary-color);
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.user-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.user-details p {
  color: var(--text-primary);
  line-height: 1.8;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.active {
  background: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background: #f8d7da;
  color: #721c24;
}

.status-badge.verified {
  background: #d1ecf1;
  color: #0c5460;
}

.status-badge.unverified {
  background: #fff3cd;
  color: #856404;
}

.status-badge.superuser {
  background: #d4a5f9;
  color: #5b21b6;
}

.status-badge.regular {
  background: #e5e7eb;
  color: #374151;
}

.error-message {
  padding: 0.75rem 1rem;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 1rem;
}

.management-section {
  background: var(--surface);
  padding: 2rem;
  border-radius: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-header h2 {
  color: var(--primary-color);
  font-size: 1.75rem;
}

.refresh-button {
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-button:hover:not(:disabled) {
  background: var(--primary-dark);
}

.refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-filter-bar {
  margin-bottom: 1.5rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.1);
}

.members-table-container {
  overflow-x: auto;
  margin-bottom: 1.5rem;
}

.members-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.members-table thead {
  background: var(--primary-color);
  color: white;
}

.members-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
}

.members-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.members-table tbody tr:hover {
  background: var(--surface);
}

.members-table tbody tr:last-child td {
  border-bottom: none;
}

.action-button {
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  margin-right: 0.5rem;
  transition: all 0.3s ease;
}

.view-button {
  background: var(--secondary-color);
  color: white;
}

.view-button:hover {
  background: #e76f51;
}

.edit-button {
  background: var(--primary-color);
  color: white;
}

.edit-button:hover {
  background: var(--primary-dark);
}

.delete-button {
  background: #dc3545;
  color: white;
}

.delete-button:hover {
  background: #c82333;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.add-button {
  padding: 0.5rem 1rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-button:hover {
  background: #218838;
  transform: translateY(-1px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h2 {
  margin: 0;
  color: var(--primary-color);
  font-size: 1.5rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-form {
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.required {
  color: #dc3545;
}

.form-group input,
.form-group select {
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.1);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
}

.cancel-button,
.submit-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button {
  background: var(--text-secondary);
  color: white;
}

.cancel-button:hover {
  background: var(--text-primary);
}

.submit-button {
  background: var(--primary-color);
  color: white;
}

.submit-button:hover:not(:disabled) {
  background: var(--primary-dark);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* View Member Modal Styles */
.view-modal {
  max-width: 800px;
}

.member-details {
  padding: 1.5rem;
}

.detail-section {
  margin-bottom: 2rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h3 {
  color: var(--primary-color);
  font-size: 1.25rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--border-color);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  color: var(--text-primary);
  font-size: 1rem;
  padding: 0.5rem 0;
  word-break: break-word;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-content {
    max-width: 100%;
    margin: 1rem;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

.member-count {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .admin-header-content {
    flex-wrap: wrap;
    height: auto;
    padding: 1rem 20px;
  }

  .admin-logo {
    order: 1;
    width: 100%;
    margin-bottom: 1rem;
  }

  .admin-nav {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    border-top: 1px solid var(--border-color);
    padding-top: 1rem;
    margin-top: 1rem;
  }

  .admin-nav-link {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }

  .admin-header-actions {
    order: 2;
  }

  .page-header h1 {
    font-size: 2rem;
  }

  .members-table {
    font-size: 0.875rem;
  }

  .members-table th,
  .members-table td {
    padding: 0.5rem;
  }

  .action-button {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
    margin-right: 0.25rem;
  }
}
</style>

